import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBusinessReviews, useReplyToReview, type ReviewRow } from '@/lib/data/reviews';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/reviews')({
  component: ReviewsPage,
});

function ReviewsPage() {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const reviewsQuery = useBusinessReviews(supabase, businessId);

  return (
    <section className="flex flex-col gap-lg">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Reviews</h1>

      {!businessId ? <p className="text-body text-ink-2">Select a property first.</p> : null}
      {reviewsQuery.isLoading ? <PageLoading /> : null}
      {reviewsQuery.isError ? <p className="text-body text-danger">Could not load reviews.</p> : null}

      {reviewsQuery.data && reviewsQuery.data.length > 0 ? (
        <ul className="flex flex-col gap-md">
          {reviewsQuery.data.map((review) => (
            <ReviewCard key={review.id} review={review} businessId={businessId} />
          ))}
        </ul>
      ) : null}

      {reviewsQuery.data && reviewsQuery.data.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-lg text-body text-ink-2">No reviews yet.</p>
      ) : null}
    </section>
  );
}

function ReviewCard({ review, businessId }: { review: ReviewRow; businessId: string | null }) {
  const { supabase } = useAuth();
  const replyMutation = useReplyToReview(supabase, businessId);
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(review.host_reply ?? '');
  const [error, setError] = useState<string | null>(null);

  return (
    <li className="rounded-card border border-border bg-surface p-lg shadow-card">
      <div className="flex items-center justify-between">
        <p className="text-body font-semibold text-ink">{review.profiles?.display_name ?? 'Guest'}</p>
        <p className="text-body text-ink-2" aria-label={`${review.rating} out of 5 stars`}>
          {'★'.repeat(review.rating)}
          {'☆'.repeat(Math.max(0, 5 - review.rating))}
        </p>
      </div>
      {review.comment ? <p className="mt-sm text-body text-ink-2">{review.comment}</p> : null}

      {review.host_reply && !editing ? (
        <div className="mt-md rounded-input border border-hair bg-surface-2 p-md">
          <p className="text-caption font-semibold text-ink-2">Your reply</p>
          <p className="mt-xs text-body text-ink">{review.host_reply}</p>
          <button type="button" onClick={() => setEditing(true)} className="mt-sm text-caption text-canyon underline">
            Edit reply
          </button>
        </div>
      ) : null}

      {!review.host_reply || editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            replyMutation.mutate(
              { reviewId: review.id, reply },
              { onSuccess: () => setEditing(false), onError: (err) => setError(err instanceof Error ? err.message : 'Could not send the reply.') },
            );
          }}
          className="mt-md flex flex-col gap-sm"
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            placeholder="Reply publicly to this review…"
            required
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
          />
          <div className="flex items-center gap-sm">
            <button
              type="submit"
              disabled={replyMutation.isPending}
              className="self-start rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
            >
              {replyMutation.isPending ? 'Sending…' : review.host_reply ? 'Update reply' : 'Reply'}
            </button>
            {editing ? (
              <button type="button" onClick={() => setEditing(false)} className="text-caption text-ink-2 underline">
                Cancel
              </button>
            ) : null}
          </div>
          {error ? <p className="text-body text-danger">{error}</p> : null}
        </form>
      ) : null}
    </li>
  );
}
